/**
 * Per-route document metadata for HandInvoice.
 *
 * Built on React 19's native metadata support: <title>, <meta> and <link>
 * elements rendered anywhere in the component tree are automatically hoisted
 * into <head>. No react-helmet needed — react-helmet-async has no React 19
 * support, and React 19's built-in hoisting is the supported path.
 *
 * Public routes (see seo.js) get a full set of SEO + Open Graph + Twitter
 * tags plus a canonical link. Every other route is private and gets only
 * `noindex` — no og/twitter/canonical, since those pages are never shared
 * or indexed.
 */
import { useLocation } from "react-router-dom";
import { getSeo, normalizePath, SITE_URL, OG_IMAGE_URL } from "../seo";

/**
 * Returns the metadata elements for the current route. React 19 hoists them
 * to <head>. Render the result inside <BrowserRouter> (see <PageMeta/>).
 */
export function usePageMeta() {
  const { pathname } = useLocation();
  const path = normalizePath(pathname);
  const seo = getSeo(path);

  // Private routes: keep them out of every index, nothing else.
  if (!seo) {
    return [
      <title key="title">HandInvoice</title>,
      <meta
        key="robots"
        name="robots"
        content="noindex, nofollow, noarchive"
      />,
    ];
  }

  const canonical = SITE_URL + path;
  const tags = [
    <title key="title">{seo.title}</title>,
    <meta key="desc" name="description" content={seo.description} />,
  ];
  if (seo.keywords) {
    tags.push(<meta key="kw" name="keywords" content={seo.keywords} />);
  }
  tags.push(
    <meta key="ogtype" property="og:type" content="website" />,
    <meta key="ogsite" property="og:site_name" content="HandInvoice" />,
    <meta key="ogtitle" property="og:title" content={seo.title} />,
    <meta key="ogdesc" property="og:description" content={seo.description} />,
    <meta key="ogurl" property="og:url" content={canonical} />,
    <meta key="ogimg" property="og:image" content={OG_IMAGE_URL} />,
    <meta key="twcard" name="twitter:card" content="summary_large_image" />,
    <meta key="twtitle" name="twitter:title" content={seo.title} />,
    <meta
      key="twdesc"
      name="twitter:description"
      content={seo.description}
    />,
    <meta key="twimg" name="twitter:image" content={OG_IMAGE_URL} />,
    <link key="canonical" rel="canonical" href={canonical} />,
  );
  return tags;
}

/**
 * Component wrapper for usePageMeta — mount inside <BrowserRouter> so it can
 * read the current route. Renders nothing visible; React 19 lifts the tags
 * into <head>.
 */
export function PageMeta() {
  return <>{usePageMeta()}</>;
}

/**
 * Renders a JSON-LD structured-data block. Inline application/ld+json
 * scripts are valid anywhere in the document; crawlers read them from the
 * body just as well as the head.
 */
export function JsonLd({ data }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
